/**
 * Parsing an API response instead of casting it.
 *
 * `response.data.data as X` tells the compiler a thing the network never
 * promised, so a field the endpoint does not send, or sends under another
 * type, only shows up later as a blank cell somewhere else. A parse puts the
 * check where the data enters, so a mismatch fails there, naming the field.
 *
 * LOOSE OBJECTS. Every schema built on these helpers accepts fields it does not
 * know about, because the endpoints send whole database rows and the client
 * only declares what it reads. A missing or wrong-typed field fails; an extra
 * one does not.
 */

import { z } from "zod";

/** One reason a body did not match, with the path to the offending field. */
export interface WireIssue {
	path: string;
	message: string;
}

/** Past this many issues the message says how many it left out. */
const MAX_ISSUES_IN_MESSAGE = 3;

/**
 * Thrown when a 2xx response does not have the shape the client declared.
 *
 * Not an HTTP error: the request succeeded. It means the client and the server
 * disagree about the contract, which is a bug to fix, not a condition to show
 * a user -- so it carries the endpoint and every issue for whoever reads the
 * console, and a message that names the first few.
 */
export class ResponseShapeError extends Error {
	readonly endpoint: string;
	readonly issues: WireIssue[];

	constructor(endpoint: string, issues: WireIssue[]) {
		const shown = issues
			.slice(0, MAX_ISSUES_IN_MESSAGE)
			.map((issue) => `${issue.path}: ${issue.message}`)
			.join("; ");
		const rest = issues.length - MAX_ISSUES_IN_MESSAGE;
		super(
			`Unexpected response from ${endpoint}: ${shown}${rest > 0 ? ` (+${rest} more)` : ""}`,
		);
		this.name = "ResponseShapeError";
		this.endpoint = endpoint;
		this.issues = issues;
	}
}

/** `data.items[0].price.amount` from zod's path segments. */
function formatPath(path: ReadonlyArray<PropertyKey>): string {
	let out = "";
	for (const segment of path) {
		if (typeof segment === "number") out += `[${segment}]`;
		else out += out ? `.${String(segment)}` : String(segment);
	}
	return out || "(body)";
}

/**
 * The success envelope every endpoint wraps its payload in.
 *
 * Only `success` and `data` are checked. `message` and `error` are for humans
 * and nothing reads them from a 2xx.
 */
export function envelope<Data extends z.ZodType>(data: Data) {
	return z.looseObject({ success: z.boolean(), data });
}

/**
 * Check a response BODY (axios `response.data`) against `dataSchema` and return
 * the payload, parsed.
 *
 * `endpoint` is a label for the error, e.g. `GET /api/v1/admin/catalog/products`.
 */
export function parseResponseData<Data extends z.ZodType>(
	body: unknown,
	dataSchema: Data,
	endpoint: string,
): z.output<Data> {
	const result = envelope(dataSchema).safeParse(body);
	if (!result.success) {
		throw new ResponseShapeError(
			endpoint,
			result.error.issues.map((issue) => ({
				path: formatPath(issue.path),
				message: issue.message,
			})),
		);
	}
	// zod cannot carry `Data` through `looseObject`'s inferred shape; the parse
	// above is what makes this narrowing true.
	return result.data.data as z.output<Data>;
}

/**
 * A page of an admin list endpoint, matching `AdminListPage<T>`.
 */
export function adminListPageSchema<Item extends z.ZodType>(item: Item) {
	return z.looseObject({
		items: z.array(item),
		total: z.number(),
		page: z.number(),
		perPage: z.number(),
		totalPages: z.number(),
	});
}

/**
 * An optional string the server sends as `null` when the column is empty.
 *
 * The declared types say `description?: string`, the nullable column says
 * `null`. Normalising at the boundary makes the declared type true instead of
 * widening it under every consumer.
 */
export const optionalText = z
	.string()
	.nullish()
	.transform((value) => value ?? undefined);

/** Like `optionalText`, for numbers. */
export const optionalNumber = z
	.number()
	.nullish()
	.transform((value) => value ?? undefined);

/**
 * A list column that is nullable in the database and `[]` by default.
 *
 * The key must be PRESENT -- a response that drops it is a contract change --
 * but a stored `null` reads as empty, which is what the declared type promises.
 */
export function listOrEmpty<Item extends z.ZodType>(item: Item) {
	return z
		.array(item)
		.nullable()
		.transform((value) => value ?? []);
}

/** The `{}` counterpart of `listOrEmpty`. */
export function recordOrEmpty<Value extends z.ZodType>(value: Value) {
	return z
		.record(z.string(), value)
		.nullable()
		.transform((record) => record ?? {});
}
